import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@material-ui/core';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { reduxForm } from 'redux-form/immutable';
import { Box } from '@mui/material';

import { selectUser } from 'entities/user/selectors';
import {
  selectPendingPartners,
  sortNewestCreatedFirst,
  sortNewestModifiedFirst,
  sortNewestRejectedFirst,
} from 'entities/partners/selectors';
import { PARTNER_STATUSES } from 'entities/partners/constants';
import { TableBox } from 'retired/Box';
import CompanyLink from 'retired/CompanyLink';
import NonprofitLink from 'retired/NonprofitLink';
import PageHeader from 'retired/PageHeader';
import TablePaginationActions from 'retired/TablePaginationActions';
import { dark, gray5 } from 'utils/color-styles';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Privileges } from 'utils/privileges';
import DialogButton from 'retired/DialogButton';

import AddPartnerNonprofitModal from './AddPartnerNonprofitModal';
import epics from './epics';
import reducer from './reducer';
import RequestCorporatePartnershipModal from './RequestCorporatePartnershipModal';
import {
  selectDisplayedPartners,
  selectError,
  selectFetchParams,
  selectLoading,
  selectStatusUpdating,
  selectTotals,
} from './selectors';
import {
  approveAction,
  denyAction,
  FORM_IDENTIFIER,
  PARTNER_DATE_FIELD,
  PARTNER_STATUS_DISPLAY_KEYS,
  setFetchParamsAction,
} from './types';
import { formatPartnerDateForPartnerStatus } from './utils';
import { SearchBox } from './SearchBox';

type SortParameters = 'name' | 'createdAt' | 'modifiedAt';
type OrderParameters = 'asc' | 'desc';

interface HeaderColumn {
  id: SortParameters;
  label: string;
  sorted?: boolean;
  colspan?: number;
  style?: Record<string, string | number>;
}

const TabItem = ({ children, isActive, ...rest }): JSX.Element => {
  const normal = {
    color: gray5,
    fontWeight: 400,
    fontSize: 14,
    marginTop: 15,
    marginBottom: 0,
    marginRight: 25,
    paddingBottom: 14,
    cursor: 'pointer',
  };
  const active = {
    ...normal,
    ...{
      color: dark,
      borderBottom: `1px solid ${dark}`,
      fontWeight: 500,
    },
  };

  return (
    <span style={isActive ? active : normal} {...rest}>
      {children}
    </span>
  );
};

const PartnerRequests: React.FC<{}> = () => {
  const dispatch = useDispatch();
  useInjectEpics({ key: 'partnerRequests', epics });
  useInjectReducer({ key: 'partnerRequests', reducer });

  const pendingPartners = useSelector(selectPendingPartners);
  const statusUpdating = useSelector(selectStatusUpdating);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const user = useSelector(selectUser);
  const history = useHistory();
  const {
    partnerStatus,
  }: {
    partnerStatus: PARTNER_STATUS_DISPLAY_KEYS;
  } = useParams();
  let displayedPartners = useSelector(selectDisplayedPartners);
  const fetchParams = useSelector(selectFetchParams);
  const totals = useSelector(selectTotals);
  const showCompanyControls = user?.isEmployee;
  const showNonprofitControls =
    !showCompanyControls &&
    [PARTNER_STATUSES.accepted, PARTNER_STATUSES.pending].includes(PARTNER_STATUSES[partnerStatus]);

  const [sortLabel, setSortLabel] = useState<{
    sortedBy: SortParameters;
    order: OrderParameters;
  }>({
    sortedBy: 'modifiedAt',
    order: 'desc',
  });

  const [addNonprofitPartnerModalOpen, setAddNonprofitPartnerModalOpen] = useState(false);
  const [requestCorporatePartnershipModalOpen, setRequestCorporatePartnershipModalOpen] =
    useState(false);

  useEffect(() => {
    if (user && !user.hasPrivilege(Privileges.PARTNER_VIEW)) {
      history.replace('/');
    }
  });

  useEffect(() => {
    const sort = PARTNER_DATE_FIELD[partnerStatus] || PARTNER_DATE_FIELD.pending;

    dispatch(
      setFetchParamsAction({
        partnerStatus: PARTNER_STATUSES[partnerStatus],
        sort,
      })
    );
  }, [partnerStatus]);

  useEffect(() => {
    dispatch(setFetchParamsAction({ sort: sortLabel.sortedBy, order: sortLabel.order }));
  }, [sortLabel]);

  switch (PARTNER_STATUSES[partnerStatus]) {
    case PARTNER_STATUSES.accepted: {
      displayedPartners = displayedPartners.sort(sortNewestModifiedFirst);
      break;
    }

    case PARTNER_STATUSES.rejected: {
      displayedPartners = displayedPartners.sort(sortNewestRejectedFirst);
      break;
    }

    case PARTNER_STATUSES.pending:
    default: {
      displayedPartners = displayedPartners.sort(sortNewestCreatedFirst);
      break;
    }
  }

  const count = typeof totals.count === 'number' ? totals.count : 0;

  const goToPage = (__: unknown, pageNum: number): void => {
    dispatch(
      setFetchParamsAction({
        pageNum,
        partnerStatus: PARTNER_STATUSES[partnerStatus],
      })
    );
  };

  const onSortedLabelClick = (item: HeaderColumn): void => {
    if (item.id === sortLabel.sortedBy) {
      const order = sortLabel.order === 'desc' ? 'asc' : 'desc';
      setSortLabel({
        sortedBy: item.id,
        order,
      });
    } else {
      setSortLabel({
        sortedBy: item.id,
        order: 'desc',
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setFetchParamsAction({
        limit: Number(event.target.value),
        partnerStatus: PARTNER_STATUSES[partnerStatus],
      })
    );
  };

  const handleCloseAddNonprofitPartnerModal = () => setAddNonprofitPartnerModalOpen(false);
  const handleRequestCorporatePartnershipModal = () =>
    setRequestCorporatePartnershipModalOpen(false);

  const selectSubmission = (matchingStatus: PARTNER_STATUS_DISPLAY_KEYS): void => {
    history.replace(`../partners/${matchingStatus}`);
  };

  const capitalizedPartnerStatusDisplayKey =
    PARTNER_STATUS_DISPLAY_KEYS[partnerStatus]?.charAt(0)?.toUpperCase() +
    PARTNER_STATUS_DISPLAY_KEYS[partnerStatus]?.slice(1);

  const columns = [
    { id: 'partner.name', label: 'Name', sorted: true, colspan: 2, style: { minWidth: '50%' } },
    PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.pending && {
      id: 'createdAt',
      label: 'Submitted on',
      sorted: true,
    },
    PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.accepted && {
      id: 'modifiedAt',
      label: 'Approved on',
      sorted: true,
    },
    PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.rejected && {
      id: 'modifiedAt',
      label: 'Denied on',
      sorted: true,
    },
    (showCompanyControls || showNonprofitControls) &&
      user?.hasPrivilege(Privileges.PARTNER_MANAGE) && {
        id: 'action',
        label: 'Approve / Reject',
        sorted: false,
        style: { textAlign: 'center' },
      },
  ].filter(Boolean);

  return (
    <>
      <AddPartnerNonprofitModal
        open={addNonprofitPartnerModalOpen}
        onClickClose={handleCloseAddNonprofitPartnerModal}
      />
      <RequestCorporatePartnershipModal
        open={requestCorporatePartnershipModalOpen}
        onClickClose={handleRequestCorporatePartnershipModal}
      />
      <Helmet title={`${capitalizedPartnerStatusDisplayKey} Affiliations`} />
      {showCompanyControls ? (
        <PageHeader
          title="Affiliations"
          onArrowClick={() => history.push('../')}
          buttonDisabled={loading}
          buttonText={user?.hasPrivilege(Privileges.PARTNER_MANAGE) ? 'Add affiliation' : undefined}
          onButtonClick={(): void => setAddNonprofitPartnerModalOpen(true)}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1 style={{ marginTop: 0 }}>Corporate Affiliations</h1>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1em',
              justifyContent: 'flex-end',
            }}
          >
            {user?.hasPrivilege(Privileges.PARTNER_MANAGE) && (
              <div>
                <Button
                  style={{ marginRight: 10 }}
                  onClick={(): void => setRequestCorporatePartnershipModalOpen(true)}
                >
                  Request an affiliation
                </Button>
              </div>
            )}

            {user?.hasPrivilege(Privileges.DEED_INTERNAL_HANDLE_NONPROFIT_NOMINATIONS) && (
              <Button
                variant="outlined"
                onClick={(): void => history.push('/nonprofit-nominations')}
              >
                Nonprofit Nominations
              </Button>
            )}
          </div>
        </div>
      )}
      <div style={{ marginBottom: '1.33em' }}>
        <TabItem
          isActive={PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.accepted}
          onClick={(): void => selectSubmission(PARTNER_STATUS_DISPLAY_KEYS.accepted)}
        >
          Approved
        </TabItem>
        <TabItem
          isActive={PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.pending}
          onClick={(): void => selectSubmission(PARTNER_STATUS_DISPLAY_KEYS.pending)}
        >
          Pending
          {Number.isFinite(pendingPartners?.size) && pendingPartners?.size > 0
            ? ` (${pendingPartners.size})`
            : ''}
        </TabItem>
        <TabItem
          isActive={PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.rejected}
          onClick={(): void => selectSubmission(PARTNER_STATUS_DISPLAY_KEYS.rejected)}
        >
          Denied
        </TabItem>
      </div>
      <div>
        <TableBox>
          {loading && <LinearProgress />}

          <SearchBox />

          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.filter(Boolean).map((item) => (
                  <TableCell
                    key={item.id}
                    size="medium"
                    colspan={item.colspan || 1}
                    style={item.style}
                  >
                    {item.sorted ? (
                      <TableSortLabel
                        direction={sortLabel.order}
                        active={sortLabel.sortedBy === item.id}
                        onClick={(): void => {
                          onSortedLabelClick(item);
                        }}
                      >
                        {item.label}
                      </TableSortLabel>
                    ) : (
                      item.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody
              style={loading ? { opacity: 0.3, cursor: 'wait', pointerEvents: 'none' } : null}
            >
              {!loading && error && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography>There was an error loading your affiliations</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!count && !loading && !error && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography>
                      {PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.accepted &&
                        `You have no accepted affiliations`}
                      {PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.rejected &&
                        `You have no rejected affiliations`}
                      {PARTNER_STATUSES[partnerStatus] === PARTNER_STATUSES.pending &&
                        `You have no pending affiliations`}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {displayedPartners.map((partner) => (
                <TableRow key={partner.id}>
                  <TableCell colspan={2} style={{ textAlign: 'left ' }}>
                    {partner.__t === 'Company' ? (
                      <CompanyLink company={partner} />
                    ) : (
                      <NonprofitLink nonprofit={partner} />
                    )}
                  </TableCell>
                  <TableCell style={{ textAlign: 'left ' }}>
                    {formatPartnerDateForPartnerStatus(partner, partnerStatus)}
                  </TableCell>
                  {showCompanyControls && user?.hasPrivilege(Privileges.PARTNER_MANAGE) ? (
                    <TableCell style={{ textAlign: 'center ' }}>
                      {statusUpdating.get(partner.id) ? (
                        <CircularProgress size={24} style={{ margin: '10px 12px' }} />
                      ) : (
                        <Box display="flex" flexDirection="row" justifyContent="center">
                          {partner.status !== PARTNER_STATUSES.accepted ? (
                            <IconButton
                              title="Approve"
                              onClick={() => dispatch(approveAction(partner.id))}
                            >
                              <CheckIcon />
                            </IconButton>
                          ) : (
                            <Tooltip title="Affiliation already approved">
                              <span>
                                <IconButton disabled>
                                  <CheckIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {partner.status !== PARTNER_STATUSES.rejected ? (
                            <DialogButton
                              title="Are you sure you want to remove this affiliation?"
                              text="By denying this affiliation, you will remove the connection between your organization and the affiliated entity."
                              confirmLabel="Confirm"
                              cancelLabel="Go back"
                              handleConfirm={() => dispatch(denyAction(partner.id))}
                              iconButton
                              tooltip="Remove the affiliation"
                            >
                              <BlockIcon />
                            </DialogButton>
                          ) : (
                            <Tooltip title="Affiliation already denied">
                              <span>
                                <IconButton disabled>
                                  <BlockIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  ) : null}
                  {showNonprofitControls && user?.hasPrivilege(Privileges.PARTNER_MANAGE) ? (
                    <TableCell style={{ textAlign: 'center ' }}>
                      {statusUpdating.get(partner.id) ? (
                        <CircularProgress size={24} style={{ margin: '10px 12px' }} />
                      ) : (
                        <Box display="flex" flexDirection="row" justifyContent="center">
                          {partner.status !== PARTNER_STATUSES.accepted ? (
                            <Tooltip title="Affiliation pending approval">
                              <span>
                                <IconButton disabled>
                                  <CheckIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Affiliation approved">
                              <span>
                                <IconButton disabled>
                                  <CheckIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {partner.status !== PARTNER_STATUSES.rejected ? (
                            <DialogButton
                              title="Are you sure you want to remove this affiliation?"
                              text="This action cannot be undone. By denying this affiliation, you will remove the connection between your organization and the affiliated entity."
                              confirmLabel="Confirm"
                              cancelLabel="Go back"
                              handleConfirm={() => dispatch(denyAction(partner.id))}
                              iconButton
                              tooltip="Remove the affiliation"
                            >
                              <BlockIcon />
                            </DialogButton>
                          ) : (
                            <Tooltip title="Affiliation denied">
                              <span>
                                <IconButton disabled>
                                  <BlockIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  size="medium"
                  colSpan={2}
                  style={{ color: '#0000008a', fontSize: '0.875rem' }}
                >
                  {[`Affiliates: ${count}`].filter(Boolean).join(' - ')}
                </TableCell>
                <TablePagination
                  colSpan={2} // make the pagination stretch full width
                  count={count || 0}
                  rowsPerPage={fetchParams.get('limit')}
                  page={fetchParams.get('pageNum')}
                  onChangePage={goToPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableBox>
      </div>
    </>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  onChange: (values, dispatch, __, previousValues) => {
    if (values.size === previousValues.size) {
      dispatch(setFetchParamsAction({}));
    }
  },
  initialValues: {
    currencies: [],
    from: null,
    to: null,
  },
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(PartnerRequests);
